import React, {useState, useContext} from 'react';
import {AxiosContext} from 'components/utils/AxiosProvider';

const usePostEndpoint = (endpoint, useCached = false, cache = false) => {
    const [submissionInProgress, setSubmissionInProgress] = useState(null);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const {client: axiosClient} = useContext(AxiosContext);

    const onFormPosted = result => {
        setSubmissionInProgress(false);
        error && setError(null);
        setResult(result.data);
    };

    const onError = error => {
        setSubmissionInProgress(false);
        result && setResult(null);
        setError(error.response?.data);
    };

    const post = (formData, params = null, endpointModifier = '') => {
        setSubmissionInProgress(true);
        return axiosClient
            .post(endpoint + endpointModifier, formData, {
                params: params,
                useCache: useCached,
                cache: cache,
            })
            .then(onFormPosted)
            .catch(onError);
    };

    const reset = () => {
        setSubmissionInProgress(null);
        setResult(null);
        setError(null);
    };

    return {
        post,
        submissionInProgress,
        result,
        error,
        reset,
    };
};

const usePostProfile = () => {
    return usePostEndpoint('/api/1/user/profile');
};

const usePostSubscribe = () => {
    return usePostEndpoint('/api/1/payments/subscribe');
};

const usePostStartApplication = () => {
    return usePostEndpoint('/api/1/payments/user/start_application');
};

const usePostUnsubscribe = () => {
    return usePostEndpoint('/api/1/payments/unsubscribe');
};

const usePostBillingAddress = () => {
    return usePostEndpoint('/api/1/payments/billing_address');
};

const usePostAdminAddRole = () => {
    return usePostEndpoint('/api/1/admin/user/add_role');
};

const usePostAdminTestUser = () => {
    return usePostEndpoint('/api/1/admin/testusers');
};

const usePostUpcomingPayments = (useCached = true, cache = true) => {
    return usePostEndpoint(
        '/api/1/admin/payments/upcoming_payments',
        useCached,
        cache
    );
};

const usePostApplicationSignatures = () => {
    return usePostEndpoint('/api/1/user/application/cg719b/sign');
}

const usePostApplicationIssue = () => {
    return usePostEndpoint('/api/1/user/application/cg719b/issue');
}

const usePostEndorsementSearch = () => {
    return usePostEndpoint('/api/1/endorsements/filter');
}

//// Portal ////
const usePostAddOrgUser = orgId => {
    return usePostEndpoint(`/api/1/portal/organization/${orgId}/user/add_user`);
};

const usePostAddOrgOwner = orgId => {
    return usePostEndpoint(`/api/1/portal/organization/${orgId}/add_org_owner`);
};

const usePostDeleteMemberInvite = orgId => {
    // must use endpointModifier '{user_id}/delete'
    return usePostEndpoint(`/api/1/portal/organization/${orgId}/user/`);
};

const usePostRemoveMember = orgId => {
    // must use endpointModifier '{user_id}/remove'
    return usePostEndpoint(`/api/1/portal/organization/${orgId}/user/`);
};

const usePostMemberAcceptInvite = orgId => {
    return usePostEndpoint(`/api/1/portal/user/${orgId}/accept`);
};

const usePostMemberRevoke = orgId => {
    return usePostEndpoint(`/api/1/portal/user/${orgId}/revoke`);
};

const withUsePostEndpoint = (endpoint, Component) => {
    return props => {
        const {post, submissionInProgress, result, error} =
            usePostEndpoint(endpoint);
        return (
            <Component
                postEndpoint={post}
                postEndpointInProgress={submissionInProgress}
                postEndpointResult={result}
                postEndpointError={error}
                {...props}
            />
        );
    };
};

//// Vessel ////
const usePostNewVessel = () => {
    return usePostEndpoint('/api/1/vessels/new');
};

//// Associate ////
const usePostAssociate = () => {
    return usePostEndpoint('/api/1/user/associate');
};

//// Admin ////

const usePostApplicationEvent = applicationId => {
    return usePostEndpoint(`/api/1/admin/application/${applicationId}/event`);
};

export default usePostEndpoint;
export {
    usePostProfile,
    usePostSubscribe,
    usePostStartApplication,
    usePostUnsubscribe,
    usePostBillingAddress,
    withUsePostEndpoint,
    usePostAdminAddRole,
    usePostAdminTestUser,
    usePostUpcomingPayments,
    usePostAddOrgUser,
    usePostDeleteMemberInvite,
    usePostMemberAcceptInvite,
    usePostMemberRevoke,
    usePostRemoveMember,
    usePostNewVessel,
    usePostAddOrgOwner,
    usePostAssociate,
    usePostApplicationEvent,
    usePostEndorsementSearch,
    usePostApplicationSignatures,
    usePostApplicationIssue,
};
